import validator from "validator";
import { TValidator } from "../../../../wizard/types";
import { normalizePhoneNumber } from "../../../../../utils";
import { isValidPhone } from "../../../../../utils/validators";
import moment from "moment";
import { UserRole } from "../../../enums/UserRole";
import { TeacherDetailTypeEnum } from "../../../../privateClasses/types";
import { HTMLTagRegEx } from "../../../../../constants";

export const formValidator = (userRole) => {
  switch (userRole) {
    case UserRole.Agent:
      return agentAccountFormValidator;
    case UserRole.School:
      return schoolAccountFormValidator;
    case UserRole.Student:
      return studentAccountFormValidator;
    case UserRole.Support:
      return supportAccountFormValidator;
    case UserRole.Teacher:
      return teacherAccountFormValidator;
    default:
      return undefined;
  }
};

const studentAccountFormValidator: TValidator = async ({ values }) => {
  const errors: { [key: string]: any } = {};

  if (!values.name) {
    errors.name = "Name is required.";
  } else if (values.name.trim().length < 3) {
    errors.name = "Name must have minimum 3 characters.";
  } else if (values.name.trim().length > 25) {
    errors.name = "Name maximum length should be 25 characters.";
  }

  if (!values.phone) {
    errors.phone = "Phone number is required.";
  } else if (!isValidPhone(normalizePhoneNumber(values.phone), null)) {
    errors.phone = "Invalid phone number.";
  }

  if (!values.email) {
    errors.email = "Email is required.";
  } else if (!validator.isEmail(values.email.trim())) {
    errors.email = "Email must be a valid email address.";
  }

  if (!values.studentProfile) {
    errors.studentProfile = {
      birth_date: "Birth date is required.",
      country: "Country is required.",
      city: "City is required.",
      citizenship: "Citizenship is required.",
      gender: "Gender is required.",
    };
  } else {
    errors.studentProfile = {};

    const date = values.studentProfile.birth_date;
    const maxAge = 70;
    const minAge = 5;
    const allowedMaxDate = moment().subtract(minAge, "years").startOf("day");
    const allowedMinDate = moment().subtract(maxAge, "years").startOf("day");

    if (!date) {
      errors.studentProfile.birth_date = "Birth date is required.";
    } else if (!date.isValid()) {
      errors.studentProfile.birth_date = "Invalid Date Format";
    } else if (date >= allowedMaxDate) {
      errors.studentProfile.birth_date = `Minimum allowed age is ${minAge} years`;
    } else if (date <= allowedMinDate) {
      errors.studentProfile.birth_date = `Maximum allowed age is ${maxAge} years`;
    }

    if (!values.studentProfile.country) {
      errors.studentProfile.country = "Country is required.";
    }

    if (!values.studentProfile.city) {
      errors.studentProfile.city = "City is required.";
    } else if (values.studentProfile.city.trim().length < 2) {
      errors.studentProfile.city = "City must have minimum 2 characters.";
    } else if (values.studentProfile.city.trim().length > 50) {
      errors.studentProfile.city =
        "City maximum length should be 50 characters.";
    }

    if (!values.studentProfile.citizenship) {
      errors.studentProfile.citizenship = "Citizenship is required.";
    }

    if (!values.studentProfile.gender) {
      errors.studentProfile.gender = "Gender is required.";
    }
  }

  if (
    Object.keys(errors.studentProfile).length > 0 ||
    Object.keys(errors).length - 1 > 0
  ) {
    throw errors;
  }
};

const supportAccountFormValidator: TValidator = async ({ values }) => {
  const errors: { [key: string]: any } = {};

  if (!values.name) {
    errors.name = "Name is required.";
  } else if (values.name.trim().length < 3) {
    errors.name = "Name must have minimum 3 characters.";
  } else if (values.name.trim().length > 25) {
    errors.name = "Name maximum length should be 25 characters.";
  }

  if (!values.phone) {
    errors.phone = "Phone number is required.";
  } else if (!isValidPhone(normalizePhoneNumber(values.phone), null)) {
    errors.phone = "Invalid phone number.";
  }

  if (!values.email) {
    errors.email = "Email is required.";
  } else if (!validator.isEmail(values.email.trim())) {
    errors.email = "Email must be a valid email address.";
  }

  if (Object.keys(errors).length > 0) {
    throw errors;
  }
};

const agentAccountFormValidator: TValidator = async ({ values }) => {
  const errors: { [key: string]: any } = {};

  if (!values.name) {
    errors.name = "Name is required.";
  } else if (values.name.trim().length < 3) {
    errors.name = "Name must have minimum 3 characters.";
  } else if (values.name.trim().length > 25) {
    errors.name = "Name maximum length should be 25 characters.";
  }

  if (!values.phone) {
    errors.phone = "Phone number is required.";
  } else if (!isValidPhone(normalizePhoneNumber(values.phone), null)) {
    errors.phone = "Invalid phone number.";
  }

  if (!values.email) {
    errors.email = "Email is required.";
  } else if (!validator.isEmail(values.email.trim())) {
    errors.email = "Email must be a valid email address.";
  }

  if (!values.agentProfile) {
    errors.agentProfile = {
      name: "Company name is required.",
      country: "Country is required.",
      title: "Position is required.",
    };
  } else {
    errors.agentProfile = {};

    if (!values.agentProfile.name) {
      errors.agentProfile.name = "Company name is required.";
    } else if (values.agentProfile.name.trim().length < 3) {
      errors.agentProfile.name = "Company name must have minimum 3 characters.";
    } else if (values.agentProfile.name.trim().length > 50) {
      errors.agentProfile.name =
        "Company name maximum length should be 50 characters.";
    }

    if (!values.agentProfile.country) {
      errors.agentProfile.country = "Country is required.";
    }

    if (
      values.agentProfile?.website &&
      !validator.isURL(values.agentProfile?.website?.trim())
    ) {
      errors.agentProfile.website = "Website must be a valid url.";
    } else if (values.agentProfile?.website?.trim().length < 3) {
      errors.agentProfile.website = "Website must have minimum 3 characters.";
    } else if (values.agentProfile?.website?.trim().length > 50) {
      errors.agentProfile.website =
        "Website maximum length should be 50 characters.";
    }

    if (!values.agentProfile.title) {
      errors.agentProfile.title = "Position is required.";
    } else if (values.agentProfile.title.trim().length < 3) {
      errors.agentProfile.title = "Position must have minimum 3 characters.";
    } else if (values.agentProfile.title.trim().length > 50) {
      errors.agentProfile.title =
        "Position maximum length should be 50 characters.";
    }
  }

  if (
    Object.keys(errors.agentProfile).length > 0 ||
    Object.keys(errors).length - 1 > 0
  ) {
    throw errors;
  }
};

const schoolAccountFormValidator: TValidator = async ({ values }) => {
  const errors: { [key: string]: any } = {};

  if (!values.name) {
    errors.name = "Representative name is required.";
  } else if (values.name.trim().length < 3) {
    errors.name = "Representative name must have minimum 3 characters.";
  } else if (values.name.trim().length > 25) {
    errors.name = "Representative name maximum length should be 25 characters.";
  }

  if (!values.phone) {
    errors.phone = "Phone number is required.";
  } else if (!isValidPhone(normalizePhoneNumber(values.phone), null)) {
    errors.phone = "Invalid phone number.";
  }

  if (!values.email) {
    errors.email = "Email is required.";
  } else if (!validator.isEmail(values.email.trim())) {
    errors.email = "Email must be a valid email address.";
  }

  if (!values.schoolProfile) {
    errors.schoolProfile = {
      school: {
        name: "School name is required.",
        country: "Country is required.",
        type: "Education type is required.",
        website: "Website is required.",
      },
    };
  } else {
    errors.schoolProfile = { school: {} };

    if (!values.schoolProfile.school.name) {
      errors.schoolProfile.school.name = "School name is required.";
    } else if (values.schoolProfile.school.name.trim().length < 5) {
      errors.schoolProfile.school.name =
        "School name must have minimum 5 characters.";
    } else if (values.schoolProfile.school.name.trim().length > 50) {
      errors.schoolProfile.school.name =
        "School name maximum length should be 50 characters.";
    }

    if (!values.schoolProfile.school.country) {
      errors.schoolProfile.school.country = "Country is required.";
    }

    if (!values.schoolProfile.school.website) {
      errors.schoolProfile.school.website = "Website is required.";
    } else if (!validator.isURL(values.schoolProfile.school.website.trim())) {
      errors.schoolProfile.school.website = "Website must be a valid url.";
    }

    if (!values.schoolProfile.school.type) {
      errors.schoolProfile.school.type = "Education type is required.";
    }
  }

  if (
    Object.keys(errors.schoolProfile.school).length > 0 ||
    Object.keys(errors).length - 1 > 0
  ) {
    throw errors;
  }
};

const teacherAccountFormValidator: TValidator = async ({ values }) => {
  const errors: { [key: string]: any } = {};

  if (!values.name) {
    errors.name = "Name is required.";
  } else if (values.name.trim().length < 3) {
    errors.name = "Name must have minimum 3 characters.";
  } else if (values.name.trim().length > 25) {
    errors.name = "Name maximum length should be 25 characters.";
  }

  if (!values.phone) {
    errors.phone = "Phone number is required.";
  } else if (!isValidPhone(normalizePhoneNumber(values.phone), null)) {
    errors.phone = "Invalid phone number.";
  }

  if (!values.email) {
    errors.email = "Email is required.";
  } else if (!validator.isEmail(values.email.trim())) {
    errors.email = "Email must be a valid email address.";
  }

  if (!values.teacherProfile) {
    errors.teacherProfile = {
      country: "Country is required.",
    };
  } else {
    errors.teacherProfile = {};

    if (!values.teacherProfile.country) {
      errors.teacherProfile.country = "Country is required.";
    }
  }

  const details = values.teacherProfile?.details || {};
  const requiredDetailTypes: TeacherDetailTypeEnum[] = [
    TeacherDetailTypeEnum.Bio,
    TeacherDetailTypeEnum.Education,
    TeacherDetailTypeEnum.Experience,
  ];

  requiredDetailTypes.forEach((type) => {
    const raw = details[`type_${type}`];

    if (!raw || !raw.replace(HTMLTagRegEx, "").trim().length) {
      if (!errors.teacherProfile.details) {
        errors.teacherProfile.details = {};
      }

      errors.teacherProfile.details[`type_${type}`] = "This field is required.";
    }
  });

  if (!values.newCv && !values.cv) {
    errors.cv = "CV is required.";
  }

  if (
    Object.keys(errors.teacherProfile).length > 0 ||
    Object.keys(errors).length - 1 > 0
  ) {
    throw errors;
  }
};
